import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ToastNotification, Notification, NotificationType } from '../types/notification'
import { wsClient } from '../lib/ws'
import { WebSocketMessage } from '../types/notification'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'

const SEEN_ANNOUNCEMENTS_KEY = 'workbeam:seenAnnouncements'
const POLL_INTERVAL_MS = 60 * 1000

interface NotificationState {
  toasts: ToastNotification[]
  notifications: Notification[]
  notificationPanelOpen: boolean
}

type NotificationAction =
  | { type: 'ADD_TOAST'; payload: ToastNotification }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'SET_IN_APP_NOTIFICATIONS'; payload: Notification[] }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'MARK_ALL_NOTIFICATIONS_READ' }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL_NOTIFICATIONS' }
  | { type: 'SET_NOTIFICATION_PANEL_OPEN'; payload: boolean }
  | { type: 'TOGGLE_NOTIFICATION_PANEL' }
  | { type: 'LOAD_NOTIFICATIONS'; payload: Notification[] }

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload]
      }
    
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      }
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications].slice(0, 200)
      }

    case 'SET_IN_APP_NOTIFICATIONS':
      const merged = [
        ...action.payload,
        ...state.notifications.filter((n) => n.source !== 'in_app')
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      return { ...state, notifications: merged }

    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload
            ? { ...notification, read: true }
            : notification
        )
      }
    
    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification => ({
          ...notification,
          read: true
        }))
      }
    
    case 'DELETE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(notification => notification.id !== action.payload)
      }
    
    case 'CLEAR_ALL_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      }
    
    case 'SET_NOTIFICATION_PANEL_OPEN':
      return {
        ...state,
        notificationPanelOpen: action.payload
      }

    case 'TOGGLE_NOTIFICATION_PANEL':
      return {
        ...state,
        notificationPanelOpen: !state.notificationPanelOpen
      }
    
    case 'LOAD_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload
      }
    
    default:
      return state
  }
}

interface NotificationContextType {
  state: NotificationState
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
  removeToast: (id: string) => void
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void
  markAsRead: (id: string, source?: 'in_app' | 'announcement') => void
  markAllAsRead: () => void
  deleteNotification: (id: string, source?: 'in_app' | 'announcement') => void
  clearAllNotifications: () => void
  openNotificationPanel: () => void
  closeNotificationPanel: () => void
  toggleNotificationPanel: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, {
    toasts: [],
    notifications: [],
    notificationPanelOpen: false
  })

  const queryClient = useQueryClient()
  const { user, isAuthenticated } = useAuth()
  const lastCheckedRef = useRef<string | null>(null)

  // Generate unique IDs
  const generateId = useCallback(() => {
    return Math.random().toString(36).substr(2, 9)
  }, [])

  // Show toast notification
  const showToast = useCallback((toast: Omit<ToastNotification, 'id'>) => {
    const id = generateId()
    dispatch({
      type: 'ADD_TOAST',
      payload: { ...toast, id }
    })
  }, [generateId])

  // Remove toast notification
  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id })
  }, [])

  // Add in-app notification
  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'createdAt'>) => {
    const id = generateId()
    const now = new Date().toISOString()
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: { ...notification, id, createdAt: now }
    })
  }, [generateId])

  // Mark notification as read
  const markAsRead = useCallback(async (id: string, source?: 'in_app' | 'announcement') => {
    if (source === 'in_app') {
      try {
        await api.patch(`/notifications/${id}/read`)
      } catch (err) {
        console.error('Failed to mark notification read:', err)
      }
    }
    dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id })
  }, [])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all')
    } catch (err) {
      console.error('Failed to mark all notifications read:', err)
    }
    dispatch({ type: 'MARK_ALL_NOTIFICATIONS_READ' })
  }, [])

  // Delete notification
  const deleteNotification = useCallback(async (id: string, source?: 'in_app' | 'announcement') => {
    if (source === 'in_app') {
      try {
        await api.delete(`/notifications/${id}`)
      } catch (err) {
        console.error('Failed to delete notification:', err)
      }
    }
    dispatch({ type: 'DELETE_NOTIFICATION', payload: id })
  }, [])

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_NOTIFICATIONS' })
  }, [])

  // Notification panel controls
  const openNotificationPanel = useCallback(() => {
    dispatch({ type: 'SET_NOTIFICATION_PANEL_OPEN', payload: true })
  }, [])

  const closeNotificationPanel = useCallback(() => {
    dispatch({ type: 'SET_NOTIFICATION_PANEL_OPEN', payload: false })
  }, [])

  const toggleNotificationPanel = useCallback(() => {
    dispatch({ type: 'TOGGLE_NOTIFICATION_PANEL' })
  }, [])

  // WebSocket event handlers
  useEffect(() => {
    const handleLeaveUpdate = (message: WebSocketMessage) => {
      const { data } = message
      
      // Update React Query cache
      queryClient.invalidateQueries({ queryKey: ['leaves'] })
      queryClient.invalidateQueries({ queryKey: ['leaveBalance'] })
      
      // Show notification to employee
      if (data.employeeId && data.status) {
        const notificationType: NotificationType = data.status === 'approved' ? 'success' : 'error'
        const title = data.status === 'approved' ? 'Leave Approved' : 'Leave Rejected'
        const message = data.status === 'approved' 
          ? `Your ${data.leaveType} leave has been approved by ${data.approvedBy}`
          : `Your ${data.leaveType} leave has been rejected${data.comments ? `: ${data.comments}` : ''}`
        
        addNotification({
          title,
          message,
          type: notificationType,
          read: false,
          userId: data.employeeId,
          data: data,
          actionUrl: `/dashboard/leaves/${data.leaveId}`
        })

        // Show toast
        showToast({
          title,
          message,
          type: notificationType,
          duration: 5000
        })
      }
    }

    const handleAttendanceCheck = (message: WebSocketMessage) => {
      const { data } = message
      
      // Update React Query cache
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
      
      // Show notification to managers/admins
      addNotification({
        title: 'Attendance Checked',
        message: `${data.employeeName} has checked in at ${new Date(data.checkInTime).toLocaleTimeString()}`,
        type: 'info',
        read: false,
        userId: 'all', // For managers/admins
        data: data,
        actionUrl: '/dashboard/attendance'
      })
    }

    // Subscribe to WebSocket events
    wsClient.on('leave:updated', handleLeaveUpdate)
    wsClient.on('attendance:checked', handleAttendanceCheck)

    if (!isAuthenticated) return

    wsClient.connect().catch(console.error)

    return () => {
      wsClient.off('leave:updated', handleLeaveUpdate)
      wsClient.off('attendance:checked', handleAttendanceCheck)
      wsClient.disconnect()
    }
  }, [addNotification, showToast, queryClient, isAuthenticated])

  // Poll for announcements and in-app notifications
  const setInAppNotifications = useCallback((notifications: Notification[]) => {
    dispatch({ type: 'SET_IN_APP_NOTIFICATIONS', payload: notifications })
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !user) return

    const poll = async () => {
      try {
        const since = lastCheckedRef.current
        const params = since ? { params: { since } } : {}
        const response = await api.get<{
          success: boolean
          data: {
            announcements: Array<{ id: string; title: string; content: string; authorName: string; createdAt: string }>
            inAppNotifications: Array<{
              id: string
              title: string
              message: string
              type: string
              read: boolean
              createdAt: string
              actionUrl: string | null
              data: unknown
            }>
            lastChecked: string
          }
        }>('/notifications', params)
        if (!response.data.success || !response.data.data) return

        const { announcements, inAppNotifications = [], lastChecked } = response.data.data
        const seenJson = sessionStorage.getItem(SEEN_ANNOUNCEMENTS_KEY)
        const seenIds = new Set<string>(seenJson ? JSON.parse(seenJson) : [])

        const isFirstPoll = lastCheckedRef.current === null

        for (const announcement of announcements) {
          if (!seenIds.has(announcement.id)) {
            seenIds.add(announcement.id)
            if (!isFirstPoll) {
              addNotification({
                title: 'New Announcement',
                message: `${announcement.authorName}: ${announcement.title}`,
                type: 'info',
                read: false,
                userId: user.id,
                data: announcement,
                actionUrl: '/dashboard/announcements',
                source: 'announcement'
              })
              showToast({
                title: 'New Announcement',
                message: announcement.title,
                type: 'info',
                duration: 5000
              })
              queryClient.invalidateQueries({ queryKey: ['announcements'] })
            }
          }
        }

        setInAppNotifications(
          inAppNotifications.map((n) => ({
            id: n.id,
            title: n.title,
            message: n.message,
            type: (n.type as NotificationType) || 'info',
            read: n.read,
            createdAt: n.createdAt,
            userId: user.id,
            data: n.data as Record<string, unknown>,
            actionUrl: n.actionUrl ?? undefined,
            source: 'in_app' as const
          }))
        )

        sessionStorage.setItem(SEEN_ANNOUNCEMENTS_KEY, JSON.stringify([...seenIds]))
        lastCheckedRef.current = lastChecked
      } catch (err) {
        console.error('Notification poll error:', err)
      }
    }

    poll()
    const interval = setInterval(poll, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [isAuthenticated, user?.id, addNotification, showToast, queryClient, setInAppNotifications])

  const contextValue = useMemo<NotificationContextType>(() => ({
    state,
    showToast,
    removeToast,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    openNotificationPanel,
    closeNotificationPanel,
    toggleNotificationPanel
  }), [state, showToast, removeToast, addNotification, markAsRead,
       markAllAsRead, deleteNotification, clearAllNotifications,
       openNotificationPanel, closeNotificationPanel, toggleNotificationPanel])

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  )
}
